import { Loader2 } from 'lucide-react'
import { useState, useCallback } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

import type { IssueStatus } from '@/hooks'

const STATUS_OPTIONS: Array<{ value: IssueStatus; label: string }> = [
  { value: 'backlog', label: '待办' },
  { value: 'todo', label: '计划中' },
  { value: 'in_progress', label: '进行中' },
  { value: 'code_review', label: '代码审查' },
  { value: 'testing', label: '测试中' },
  { value: 'done', label: '已完成' },
  { value: 'blocked', label: '已阻塞' },
  { value: 'cancelled', label: '已取消' },
]

const STATUS_COLORS: Record<IssueStatus, string> = {
  backlog: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  todo: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  code_review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  testing: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  done: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
}

interface InlineStatusSelectProps {
  /** 当前状态 */
  value: IssueStatus
  /** 状态变更回调 */
  onChange: (status: IssueStatus) => Promise<void>
  /** 是否可编辑 */
  canEdit?: boolean
  /** 自定义类名 */
  className?: string
  /** 保存成功回调 */
  onSaveSuccess?: () => void
  /** 保存失败回调 */
  onSaveError?: (error: Error) => void
  /** 是否显示为紧凑模式 */
  compact?: boolean
  /** 状态变为已完成时的回调 */
  onStatusDone?: () => void
}

/**
 * 任务状态行内编辑组件
 * 点击状态徽章直接弹出选择器，选择后自动保存
 */
export function InlineStatusSelect({
  value,
  onChange,
  canEdit = true,
  className,
  onSaveSuccess,
  onSaveError,
  compact = false,
  onStatusDone,
}: InlineStatusSelectProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleChange = useCallback(async (newValue: string) => {
    if (newValue === value || isSaving) { return }

    setIsSaving(true)
    setIsOpen(false)

    try {
      await onChange(newValue as IssueStatus)
      onSaveSuccess?.()
      // 当状态变为已完成时，触发工时记录提示
      if (newValue === 'done') {
        onStatusDone?.()
      }
    } catch (error) {
      onSaveError?.(error as Error)
    } finally {
      setIsSaving(false)
    }
  }, [value, onChange, onSaveSuccess, onSaveError, isSaving, onStatusDone])

  // 只读模式：显示状态徽章
  if (!canEdit) {
    return (
      <StatusBadge status={value} compact={compact} className={className} />
    )
  }

  return (
    <Select
      value={value}
      onValueChange={(v) => void handleChange(v)}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={isSaving}
    >
      <SelectTrigger
        className={cn(
          'h-auto border-0 bg-transparent p-0 shadow-none focus:ring-0',
          'hover:opacity-80 transition-opacity',
          className
        )}
      >
        <SelectValue>
          <StatusBadge status={value} compact={compact} loading={isSaving} />
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                  STATUS_COLORS[option.value]
                )}
              >
                {option.label}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface StatusBadgeProps {
  status: IssueStatus
  compact?: boolean
  loading?: boolean
  className?: string
}

/**
 * 状态徽章组件
 */
export function StatusBadge({ status, compact = false, loading = false, className }: StatusBadgeProps) {
  const option = STATUS_OPTIONS.find((opt) => opt.value === status)

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        compact ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-0.5 text-xs',
        STATUS_COLORS[status],
        className
      )}
    >
      {loading && <Loader2 className="h-3 w-3 animate-spin" />}
      {option?.label ?? status}
    </span>
  )
}

/**
 * 获取状态标签
 */
export function getStatusLabel(status: IssueStatus): string {
  return STATUS_OPTIONS.find((opt) => opt.value === status)?.label ?? status
}

/**
 * 获取状态颜色类名
 */
export function getStatusColor(status: IssueStatus): string {
  return STATUS_COLORS[status]
}
